/**
 * Унифицированный компонент статуса (Активен/Неактивен)
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Users.tsx — статус пользователя
 * - Templates.tsx — статус шаблона
 * - Services.tsx — статус услуги
 * - Admin.tsx — статус тенанта
 * - AutoReplies.tsx — статус правила
 * 
 * Связан с: components/ui/Badge.tsx, components/ui/index.ts
 */
import { Badge } from './Badge';
import { cn } from '@/utils/cn';
import { Check, X, Clock, AlertTriangle } from 'lucide-react';

export type StatusType = 'active' | 'inactive' | 'pending' | 'warning' | 'error';

export interface StatusBadgeProps {
  /** Статус для отображения */
  status: StatusType | boolean;
  /** Кастомный текст (по умолчанию: Активен/Неактивен) */
  activeText?: string;
  inactiveText?: string;
  pendingText?: string;
  warningText?: string;
  errorText?: string;
  /** Показывать иконку */
  showIcon?: boolean;
  /** Размер: sm, md */
  size?: 'sm' | 'md';
  /** Дополнительные классы */
  className?: string;
}

const STATUS_CONFIG: Record<StatusType, {
  variant: 'success' | 'error' | 'warning' | 'info' | 'secondary';
  icon: React.ReactNode;
  defaultText: string;
}> = {
  active: {
    variant: 'success',
    icon: <Check className="w-3 h-3" />,
    defaultText: 'Активен',
  },
  inactive: {
    variant: 'error',
    icon: <X className="w-3 h-3" />,
    defaultText: 'Неактивен',
  },
  pending: {
    variant: 'warning',
    icon: <Clock className="w-3 h-3" />,
    defaultText: 'Ожидает',
  },
  warning: {
    variant: 'warning',
    icon: <AlertTriangle className="w-3 h-3" />,
    defaultText: 'Внимание',
  },
  error: {
    variant: 'error',
    icon: <X className="w-3 h-3" />,
    defaultText: 'Ошибка',
  },
};

/**
 * StatusBadge — унифицированный компонент статуса
 * 
 * @example
 * // Boolean статус
 * <StatusBadge status={user.is_active} />
 * 
 * @example
 * // Строковый статус с кастомным текстом
 * <StatusBadge 
 *   status="pending" 
 *   pendingText="На модерации"
 *   showIcon 
 * />
 */
export function StatusBadge({
  status,
  activeText,
  inactiveText,
  pendingText,
  warningText,
  errorText,
  showIcon = false,
  size = 'md',
  className,
}: StatusBadgeProps) {
  // Нормализуем boolean в StatusType
  const normalizedStatus: StatusType = typeof status === 'boolean' 
    ? (status ? 'active' : 'inactive')
    : status;

  const config = STATUS_CONFIG[normalizedStatus];
  
  // Определяем текст
  const textMap: Record<StatusType, string | undefined> = {
    active: activeText,
    inactive: inactiveText,
    pending: pendingText,
    warning: warningText,
    error: errorText,
  };
  const text = textMap[normalizedStatus] || config.defaultText;

  const sizeClasses = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1';

  return (
    <Badge 
      variant={config.variant}
      className={cn(
        'inline-flex items-center gap-1',
        sizeClasses,
        className
      )}
    >
      {showIcon && config.icon}
      {text}
    </Badge>
  );
}
